import { useEffect, useState } from "react";
import type { ChangeEvent } from "react";
import { omit } from "lodash";
import { t } from "@/i18n-js/instance";
import type {
  PaywallPrice,
  PaywallPriceInterval,
} from "@/react/types/PaywallPrice";
import {
  DEFAULT_INTERVAL_OPTION,
  INTERVAL_OPTION_MAPPER,
  type IntervalOption,
  ONETIME,
  SUBSCRIPTION,
} from "../constants";

interface PaywallPriceWithTempId extends Omit<PaywallPrice, "id"> {
  id?: number;
  interval_label?: string;
  tempId?: string;
}

// Type for new prices being created - only includes the fields we actually set
interface NewPaywallPrice {
  amount: number;
  id?: number;
  interval: PaywallPriceInterval | undefined;
  interval_count: number | undefined;
  interval_label: string;
  preferred: boolean;
  price_type: string;
  tempId: string;
}

interface PaywallPriceChange {
  _destroy?: string;
  amount?: number;
  id?: number;
  interval?: PaywallPriceInterval;
  interval_count?: number;
  interval_label?: string;
  preferred?: boolean;
  price_type?: string;
  tempId?: string;
}

const generateTempId = (): string =>
  // eslint-disable-next-line sonarjs/pseudo-random -- Disabled for plugin version update. Fix when possible
  `temp-id-${Math.floor(Math.random() * 1000000)}`;

export const usePaywallPrices = (
  paywallPrices: PaywallPrice[] = [],
  pricesMaxCount: number,
) => {
  const [currentPaywallPrices, setCurrentPaywallPrices] = useState<
    (PaywallPriceWithTempId | NewPaywallPrice)[]
  >(paywallPrices || []);
  const [intervalOption, setIntervalOption] = useState<IntervalOption>(
    DEFAULT_INTERVAL_OPTION,
  );
  const [amount, setAmount] = useState<string>("");
  const [changes, setChanges] = useState<string[]>([]);
  const [hasSubscriptionPrices, setHasSubscriptionPrices] =
    useState<boolean>(false);

  useEffect(() => {
    const calculateChanges = () => {
      // Get added elements
      const added: PaywallPriceChange[] = currentPaywallPrices
        .filter(paywallPrice => !!paywallPrice.tempId)
        .map(filtered => omit(filtered, ["tempId", "interval_label"]));

      // Get removed elements
      const removed: PaywallPriceChange[] = paywallPrices
        .filter(
          paywallPrice =>
            !currentPaywallPrices.some(
              currPaywallPrice => currPaywallPrice.id === paywallPrice.id,
            ),
        )
        .map(removedPaywallPrice => ({
          ...removedPaywallPrice,
          _destroy: "1",
        }));

      // Check for preferred element changes
      const prevPreferred = paywallPrices.find(
        paywallPrice => paywallPrice.preferred,
      );
      const currPreferred = currentPaywallPrices.find(
        paywallPrice => paywallPrice.preferred,
      );
      const edited: PaywallPriceChange[] = [];

      // Check for preferred element changes
      if (prevPreferred?.id && prevPreferred?.id !== currPreferred?.id) {
        // Check that prevPreferred still exists
        const prevPreferredStillExists = currentPaywallPrices.find(
          paywallPrice => paywallPrice.id === prevPreferred?.id,
        );
        if (prevPreferredStillExists) {
          edited.push({
            id: prevPreferred.id,
            preferred: false,
          });
        }
      }

      // Insert new preferred if it is not new
      if (
        typeof currPreferred?.id !== "undefined" &&
        prevPreferred?.id !== currPreferred?.id
      ) {
        edited.push({
          id: currPreferred.id,
          preferred: true,
        });
      }

      const allChanges = [...added, ...removed, ...edited];
      setChanges(allChanges.map(change => JSON.stringify(change)));

      setHasSubscriptionPrices(
        currentPaywallPrices.some(paywallPrice => paywallPrice.interval),
      );
    };

    calculateChanges();
  }, [currentPaywallPrices, paywallPrices]);

  const onIntervalOptionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setIntervalOption(event.target.value as IntervalOption);
  };

  const onAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputAmount = event.target.value;
    if (!inputAmount) {
      setAmount("");
      return;
    }
    const numericAmount = parseFloat(inputAmount);
    const validatedAmount = numericAmount < 1 ? 1 : numericAmount;
    setAmount(validatedAmount.toString());
  };

  const resetValues = () => {
    setIntervalOption(DEFAULT_INTERVAL_OPTION);
    setAmount("");
  };

  const onAdd = (event?: { preventDefault: () => void }) => {
    event?.preventDefault();
    if (
      !intervalOption ||
      !amount ||
      currentPaywallPrices.length > pricesMaxCount
    ) {
      return;
    }

    const intervalOptionValues = INTERVAL_OPTION_MAPPER[intervalOption];
    const price_type = !intervalOptionValues.interval ? ONETIME : SUBSCRIPTION;
    const integerAmount = Math.round(parseFloat(amount) * 100);

    const newPrice: NewPaywallPrice = {
      tempId: generateTempId(),
      amount: integerAmount,
      interval: intervalOptionValues.interval,
      interval_count: intervalOptionValues.interval_count,
      interval_label: t(
        `settings.paywall_prices.form.interval_select_options.${intervalOptionValues.interval_label}_label`,
      ),
      price_type,
      preferred: false,
    };

    setCurrentPaywallPrices([...currentPaywallPrices, newPrice]);
    resetValues();
  };

  const onRemove = (atIndex: number) => {
    if (atIndex < 0 || atIndex >= currentPaywallPrices.length) {
      return;
    }
    const newCurrentPaywallPrices = [...currentPaywallPrices];
    newCurrentPaywallPrices.splice(atIndex, 1);
    setCurrentPaywallPrices(newCurrentPaywallPrices);
  };

  const onSetPreferred = (atIndex: number) => {
    if (atIndex < 0 || atIndex >= currentPaywallPrices.length) {
      return;
    }

    const newCurrentPaywallPrices = [...currentPaywallPrices];
    const prevPreferredIndex = newCurrentPaywallPrices.findIndex(
      paywallPrice => paywallPrice.preferred,
    );

    // Update previous preferred item if it's different from the new one
    if (prevPreferredIndex !== atIndex) {
      const isValidIndex =
        typeof prevPreferredIndex !== "undefined" && prevPreferredIndex >= 0;
      if (isValidIndex) {
        newCurrentPaywallPrices[prevPreferredIndex] = {
          ...newCurrentPaywallPrices[prevPreferredIndex],
          preferred: false,
        };
      }
    }

    newCurrentPaywallPrices[atIndex] = {
      ...newCurrentPaywallPrices[atIndex],
      preferred: true,
    };
    setCurrentPaywallPrices(newCurrentPaywallPrices);
  };

  return {
    currentPaywallPrices,
    intervalOption,
    onIntervalOptionChange,
    amount,
    onAmountChange,
    onAdd,
    onRemove,
    onSetPreferred,
    changes,
    hasSubscriptionPrices,
  };
};
